import React, { useEffect, useState } from 'react';
import {
  Home as HomeOutlined,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { Gradient } from '../AdminHeader/Gradient.js';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n/index';

const gradient = new Gradient();

const LEGAL_ENTITY = 'C.A.H. Media LLC';
const BUSINESS_ADDRESS = '19 NW 7th Ave, Fort Lauderdale, FL 33311';
const SUPPORT_EMAIL = 'support@headshotsnow.ai';

const Contact = () => {
  const [currentLanguage, setCurrentLanguage] = useState('');
  const [form, setForm] = useState({ name: '', email: '', orderId: '', message: '' });

  const { t } = useTranslation();

  useEffect(() => {
    gradient.initGradient('#canvas-gradient');
    window.scrollTo(0, 0);
    return () => { };
  }, []);

  useEffect(() => {
    setCurrentLanguage(i18n.language);
  }, [i18n.language]);

  const homeHref = currentLanguage === 'en' ? '/' : `/${currentLanguage}/`;

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const subject = encodeURIComponent(`Support request${form.orderId ? ` — Order ${form.orderId}` : ''}`);
    const body = encodeURIComponent(
      `Name: ${form.name}\nEmail: ${form.email}\nOrder ID: ${form.orderId || '(none)'}\n\n${form.message}`
    );
    window.location.href = `mailto:${SUPPORT_EMAIL}?subject=${subject}&body=${body}`;
  };

  return (
    <>
      <main style={{ background: '#000' }}>
        <div className="landing-cover" data-controller="gradient-animation">
          <div className="container">
            <div className="d-flex flex-column align-items-center">
              <h1 className="display-1 fw-semi-bold text-white text-center mb-5 mb-sm-4">Contact &amp; Support</h1>
              <Link to={homeHref} className="btn btn-soft-light btn-sm rounded-pill tag text-nowrap">
                <HomeOutlined />
                {t('button.backHome')}
              </Link>
            </div>
          </div>
          <canvas id="canvas-gradient" data-transition-in />
          <div id="canvas-overlay" />
        </div>
      </main>
      <section className="bg-white text-black">
        <div className="container mx-auto p-6 max-w-4xl">
          <div className="grid md:grid-cols-2 gap-10">
            <div>
              <h2 className="text-2xl font-bold mb-3">Get in touch</h2>
              <p className="mb-4">
                We&rsquo;re here to help with orders, headshot results, billing, and data requests.
              </p>
              <ul className="mb-4 space-y-2">
                <li><span className="font-semibold">Support email:</span>{' '}
                  <a className="underline" href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
                </li>
                <li><span className="font-semibold">Business:</span> {LEGAL_ENTITY}</li>
                <li><span className="font-semibold">Address:</span> {BUSINESS_ADDRESS}</li>
                <li><span className="font-semibold">Response time:</span> within 1 business day</li>
              </ul>
              <p className="text-sm text-gray-600">
                For data deletion requests, email us from the address on your account and we&rsquo;ll process it promptly.
                See our <Link to="/legal" className="underline">Privacy Policy</Link>.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold mb-3">Send us a message</h2>
              <form onSubmit={handleSubmit} className="space-y-3">
                <input
                  className="w-full border border-gray-300 rounded-md p-2"
                  type="text" name="name" placeholder="Your name" value={form.name}
                  onChange={handleChange} required
                />
                <input
                  className="w-full border border-gray-300 rounded-md p-2"
                  type="email" name="email" placeholder="Your email" value={form.email}
                  onChange={handleChange} required
                />
                <input
                  className="w-full border border-gray-300 rounded-md p-2"
                  type="text" name="orderId" placeholder="Order ID (optional)" value={form.orderId}
                  onChange={handleChange}
                />
                <textarea
                  className="w-full border border-gray-300 rounded-md p-2"
                  name="message" rows={5} placeholder="How can we help?" value={form.message}
                  onChange={handleChange} required
                />
                <button
                  type="submit"
                  className="w-full bg-cyan-600 hover:bg-cyan-700 text-white font-semibold rounded-md py-2 transition-colors"
                >
                  Send message
                </button>
                <p className="text-xs text-gray-500">
                  This opens your email app addressed to {SUPPORT_EMAIL}. You can also email us directly.
                </p>
              </form>
            </div>
          </div>

          <p className="mt-8 text-sm text-gray-600">
            Helpful links:{' '}
            <Link to="/legal" className="underline">Terms &amp; Privacy</Link>,{' '}
            <Link to="/refund" className="underline">Refund Policy</Link>,{' '}
            <Link to="/delivery" className="underline">Delivery Policy</Link>,{' '}
            <Link to="/acceptable-use" className="underline">Acceptable Use</Link>.
          </p>
        </div>
      </section>
    </>
  );
};

export default Contact;
